// Колоды: список колод, выбор Героя, редактор колоды (ui-spec.md §5).
// Подключаем к реальным данным из @data (cards-*.js + heroes-*.js).

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ScreenLayout } from '../components/ScreenLayout';
import { HeroAvatar } from '../components/HeroAvatar';
import { useTelegram } from '../telegram/useTelegram';
import { useGameStore } from '../store/useGameStore';
import { listDecks, updateDeck, deleteDeck, type DeckDto } from '../api/client';
import { useToast } from '../components/Toast';
import { useTooltipFlow } from '../tutorial/useTooltipFlow';
import { useTr } from '../i18n/useTr';
import { trHeroName } from '../i18n/serverStrings';
import styles from './DecksScreens.module.css';

const FAVORITE_LIMIT = 3;

// Импорт реальных данных Героев — named export `heroes` в каждом файле.
// allowJs: true в tsconfig + JSDoc позволяют TS вывести типы автоматически.
import { heroes as heroesDeus } from '@data/heroes-deus.js';
import { heroes as heroesSyn } from '@data/heroes-syntetics.js';
import { heroes as heroesGlb } from '@data/heroes-global.js';
import { heroes as heroesLiv } from '@data/heroes-livlife.js';
// Нейтральный Герой «Лидер улицы» — стартовый у каждого игрока.
import { heroes as heroesNeutral } from '@data/heroes-neutral.js';

type Faction = 'deus' | 'syntetics' | 'global' | 'livlife' | 'neutral';

interface HeroTemplate {
  id: string;
  name_ru: string;
  faction: Faction;
  atk: number;
  armor: number;
  hp: number;
  ability_text?: { active?: string; trigger?: string };
}

const HEROES_BY_FACTION: Record<Faction, HeroTemplate[]> = {
  deus:      (heroesDeus    as HeroTemplate[]).filter(h => h.faction === 'deus'),
  syntetics: (heroesSyn     as HeroTemplate[]).filter(h => h.faction === 'syntetics'),
  global:    (heroesGlb     as HeroTemplate[]).filter(h => h.faction === 'global'),
  livlife:   (heroesLiv     as HeroTemplate[]).filter(h => h.faction === 'livlife'),
  neutral:   (heroesNeutral as HeroTemplate[]).filter(h => h.faction === 'neutral')
};

const FACTION_LABELS: Record<Faction, string> = {
  deus: 'Деус', syntetics: 'Синтетикс', global: 'Глобал', livlife: 'Лив Лайф',
  neutral: 'Улица'
};
const FACTION_LABELS_EN: Record<Faction, string> = {
  deus: 'Deus', syntetics: 'Synthetics', global: 'Global', livlife: 'Liv Life',
  neutral: 'Street'
};
const FACTION_COLOR: Record<Faction, string> = {
  deus: 'var(--deus)', syntetics: 'var(--syn)', global: 'var(--gsec)', livlife: 'var(--liv)',
  neutral: 'var(--neon-li)'
};

// Список купленных Героев теперь берётся из Zustand store
// (useGameStore.heroesOwned). Это синхронизировано с покупками в Магазине
// и Reward-экраном после туториала.

function fmtRelativeTime(iso: string, isEn: boolean): string {
  if (!iso) return '';
  try {
    const t = new Date(iso).getTime();
    if (!t) return '';
    const diffSec = Math.floor((Date.now() - t) / 1000);
    if (diffSec < 60) return isEn ? 'just now' : 'только что';
    if (diffSec < 3600) return isEn
      ? `${Math.floor(diffSec / 60)} min ago`
      : `${Math.floor(diffSec / 60)} мин назад`;
    if (diffSec < 86400) return isEn
      ? `${Math.floor(diffSec / 3600)} h ago`
      : `${Math.floor(diffSec / 3600)} ч назад`;
    if (diffSec < 7 * 86400) return isEn
      ? `${Math.floor(diffSec / 86400)} d ago`
      : `${Math.floor(diffSec / 86400)} дн назад`;
    return new Date(iso).toLocaleDateString(isEn ? 'en-US' : 'ru-RU', { day: '2-digit', month: '2-digit' });
  } catch { return ''; }
}

// ============================================================
// 1. ВСЕ КОЛОДЫ
// ============================================================

export function DecksScreen() {
  const navigate = useNavigate();
  const { hapticTap, hapticSuccess, hapticError } = useTelegram();
  const toast = useToast(s => s.show);
  const storeDecks = useGameStore(s => s.decks);

  // ФТУЕ.6: tooltip-флоу первого входа в Колоды.
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const factionLabels = isEn ? FACTION_LABELS_EN : FACTION_LABELS;
  useTooltipFlow('decks', () => [
    {
      id: 'd-intro',
      coach: {
        title: tr('Колоды', 'Decks'),
        body: tr(
          'Тут все твои колоды. Лимит 5 (расширяется БП-перком до 10). Каждая колода — конкретный Герой + 24 карты. Можно отметить 3 как «избранные» для быстрого выбора в PvP.',
          "All your decks here. Limit 5 (expands to 10 via BP perk). Each deck — a specific Hero + 24 cards. Mark 3 as «favorites» for quick PvP selection."
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'd-build',
      coach: {
        title: tr('Состав колоды', 'Deck composition'),
        body: tr(
          '24 карты: 15 Common + 6 Rare + 2 Epic + 1 Legendary. Common — без лимита копий (макс 3), Rare макс 2, Epic 1, Legendary 1. Нейтральные карты можно совмещать с любой корпой.',
          '24 cards: 15 Common + 6 Rare + 2 Epic + 1 Legendary. Common — up to 3 copies, Rare max 2, Epic 1, Legendary 1. Neutral cards mix with any corp.'
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'd-editor',
      coach: {
        title: tr('Редактор колоды', 'Deck editor'),
        body: tr(
          'Тап по колоде → редактор. В каталоге фильтры «Только мои → Не в колоде → Все», по типу (Корпа/Нейтрал), по редкости. Тап по карте — её добавляешь/убираешь.',
          "Tap a deck → editor. Catalog has filters «Mine only → Not in deck → All», by type (Corp/Neutral), by rarity. Tap a card to add/remove it."
        ),
        showNext: true, showSkip: true
      }
    },
    {
      id: 'd-winrate',
      coach: {
        title: tr('Винрейт колоды', 'Deck winrate'),
        body: tr(
          'Под каждой колодой — её winrate за последние 50 матчей. Если меньше 40% — стоит пересобрать. Если выше 55% — ты в форме!',
          "Under each deck — its winrate over the last 50 matches. Below 40% — rebuild it. Above 55% — you're cooking!"
        ),
        showNext: true, showSkip: false,
        nextLabel: tr('Понял', 'Got it')
      }
    }
  ]);
  // Локальный список из /api/decks с winrate и актуальным isFavorite.
  // При первом рендере используем store как preview (без winrate).
  const [decks, setDecks] = useState<DeckDto[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const list = await listDecks();
      setDecks(list);
    } catch {
      // offline — оставляем что было
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Если сервер ещё не ответил — отрисуем preview из store (без winrate).
  const viewDecks: (DeckDto | (typeof storeDecks)[number])[] = decks.length > 0 || !loading ? decks : storeDecks;

  const favoriteCount = decks.filter(d => d.isFavorite).length;

  const toggleFavorite = async (d: DeckDto, e: React.MouseEvent) => {
    e.stopPropagation();
    hapticTap();
    const willFav = !d.isFavorite;
    if (willFav && favoriteCount >= FAVORITE_LIMIT) {
      hapticError();
      toast(tr(
        `Лимит ${FAVORITE_LIMIT} избранных колод. Сними звезду с другой.`,
        `Favorites limit is ${FAVORITE_LIMIT}. Un-star another deck first.`
      ), 'amber');
      return;
    }
    // Оптимистичный апдейт.
    setDecks(prev => prev.map(x => x.id === d.id ? { ...x, isFavorite: willFav } : x));
    try {
      await updateDeck(d.id, { isFavorite: willFav });
      hapticSuccess();
    } catch (err: any) {
      // Откат на ошибку.
      setDecks(prev => prev.map(x => x.id === d.id ? { ...x, isFavorite: d.isFavorite } : x));
      hapticError();
      const msg = String(err?.message || '');
      if (msg.includes('favorite_limit_reached')) {
        toast(tr(`Лимит ${FAVORITE_LIMIT} избранных колод`, `Favorites limit is ${FAVORITE_LIMIT}`), 'amber');
      } else {
        toast(tr('Не удалось сохранить', "Couldn't save"), 'red');
      }
    }
  };

  // Удаление колоды (Этап 56). Сервер: DELETE /api/decks/:id.
  const onDelete = async (d: DeckDto, e: React.MouseEvent) => {
    e.stopPropagation();
    hapticTap();
    const ok = window.confirm(tr(
      `Удалить колоду «${d.name}»? Это действие необратимо.`,
      `Delete deck «${d.name}»? This can't be undone.`
    ));
    if (!ok) return;
    // Оптимистичный апдейт списка + store.
    const prev = decks;
    setDecks(p => p.filter(x => x.id !== d.id));
    useGameStore.setState(s => ({ decks: s.decks.filter((x: any) => x.id !== d.id) }));
    try {
      await deleteDeck(d.id);
      hapticSuccess();
      toast(tr('Колода удалена', 'Deck deleted'), 'amber');
    } catch {
      setDecks(prev); // откат
      hapticError();
      toast(tr('Не удалось удалить', "Couldn't delete"), 'red');
    }
  };

  // Помощник: правильное склонение «матч/матча/матчей» (RU) или «match/matches» (EN).
  const matchesWord = (n: number): string => {
    if (isEn) return n === 1 ? 'match' : 'matches';
    return n === 1 ? '' : n < 5 ? 'а' : 'ей';
  };

  return (
    <ScreenLayout title={tr('КОЛОДЫ', 'DECKS')} backTo="/home">
      {decks.length > 0 && (
        <div style={{
          display: 'flex', justifyContent: 'space-between',
          alignItems: 'center', padding: '4px 2px 10px',
          fontSize: 11, letterSpacing: '0.08em', color: 'var(--t-md)'
        }}>
          <span>{tr('ВСЕГО', 'TOTAL')}: {decks.length}</span>
          <span style={{ color: favoriteCount > 0 ? 'var(--neon-am)' : 'var(--t-lo)' }}>
            ★ {favoriteCount}/{FAVORITE_LIMIT}
          </span>
        </div>
      )}

      <div className={styles.decksList}>
        {(viewDecks as DeckDto[]).map(d => {
          const isServerDeck = 'matches' in d;
          const winrate = isServerDeck ? (d as DeckDto).winrate : null;
          const matches = isServerDeck ? ((d as DeckDto).matches ?? 0) : 0;
          const cardCount = isServerDeck
            ? Array.isArray((d as any).cards) ? (d as any).cards.reduce((a: number, c: any) => a + (c.count || 0), 0) : 0
            : (d as any).cardCount;
          return (
            <button
              key={d.id}
              className={`${styles.deckCard} cut-m`}
              style={{ boxShadow: `inset 0 0 0 0.5px ${FACTION_COLOR[d.faction] || 'var(--line)'}` }}
              onClick={() => { hapticTap(); navigate(`/decks/editor?faction=${d.faction}&hero=${d.heroId}&id=${d.id}`); }}
            >
              <HeroAvatar id={d.heroId} size={48} faction={d.faction} />
              <div className={styles.deckInfo}>
                <div className={styles.deckName}>{d.name}</div>
                <div className={styles.deckSub}>
                  {factionLabels[d.faction] || d.faction} · {cardCount} {tr('карт', 'cards')} · {fmtRelativeTime(d.updatedAt, isEn)}
                </div>
                <div className={styles.deckSub} style={{ marginTop: 2 }}>
                  {winrate !== null && winrate !== undefined && matches > 0 ? (
                    <span>
                      <span style={{ color: winrate >= 50 ? 'var(--neon-li)' : '#ff7a7a', fontWeight: 600 }}>
                        {winrate}%
                      </span>
                      <span style={{ color: 'var(--t-lo)' }}> · {matches} {isEn ? matchesWord(matches) : `матч${matchesWord(matches)}`}</span>
                    </span>
                  ) : (
                    <span style={{ color: 'var(--t-lo)' }}>{tr('винрейт: — (нет матчей)', 'winrate: — (no matches)')}</span>
                  )}
                </div>
              </div>
              {isServerDeck && (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div
                    className={styles.deckMenu}
                    onClick={(e) => toggleFavorite(d as DeckDto, e)}
                    style={{
                      color: (d as DeckDto).isFavorite ? 'var(--neon-am)' : 'var(--t-lo)',
                      fontSize: 22, padding: '0 6px', cursor: 'pointer',
                      userSelect: 'none'
                    }}
                    title={(d as DeckDto).isFavorite ? tr('Снять из избранного', 'Remove from favorites') : tr('В избранное', 'Add to favorites')}
                  >
                    {(d as DeckDto).isFavorite ? '★' : '☆'}
                  </div>
                  <div
                    onClick={(e) => onDelete(d as DeckDto, e)}
                    style={{
                      color: 'var(--t-lo)', fontSize: 15, padding: '0 6px',
                      cursor: 'pointer', userSelect: 'none', lineHeight: 1
                    }}
                    title={tr('Удалить колоду', 'Delete deck')}
                  >
                    🗑
                  </div>
                </div>
              )}
            </button>
          );
        })}
        {!loading && decks.length === 0 && (
          <div style={{ padding: 30, textAlign: 'center', color: 'var(--t-md)', fontSize: 12 }}>
            {tr(
              'Колод пока нет. Создай первую — кнопка ниже.',
              "No decks yet. Build your first — button below."
            )}
          </div>
        )}
      </div>

      <button
        className={`${styles.createBtn} cd`}
        onClick={() => { hapticTap(); navigate('/decks/pick'); }}
      >
        + {tr('СОЗДАТЬ КОЛОДУ', 'CREATE DECK')}
      </button>
    </ScreenLayout>
  );
}

// ============================================================
// 2. ВЫБОР ГЕРОЯ
// ============================================================

export function HeroPickScreen() {
  const navigate = useNavigate();
  const { hapticTap, hapticError } = useTelegram();
  const [tab, setTab] = useState<Faction>('neutral');
  const heroesOwned = useGameStore(s => s.heroesOwned);
  const tr = useTr();
  const isEn = tr('ru', 'en') === 'en';
  const factionLabels = isEn ? FACTION_LABELS_EN : FACTION_LABELS;

  const heroes = HEROES_BY_FACTION[tab] || [];

  const onHero = (h: HeroTemplate) => () => {
    if (heroesOwned.has(h.id)) {
      hapticTap();
      // Передаём faction + hero в редактор через query — он использует
      // их для загрузки карт нужной фракции и проверки exclusive_hero.
      navigate(`/decks/editor?faction=${h.faction}&hero=${h.id}`);
    } else {
      hapticError();
      const heroName = trHeroName(h.id, h.name_ru);
      const ok = window.confirm(tr(
        `Герой «${heroName}» не куплен. Открыть Магазин?`,
        `Hero «${heroName}» not owned. Open Shop?`
      ));
      if (ok) navigate('/shop');
    }
  };

  return (
    <ScreenLayout title={tr('ВЫБОР ГЕРОЯ', 'PICK A HERO')} backTo="/decks">
      <div className={styles.factionTabs}>
        {(['neutral', 'deus', 'syntetics', 'global', 'livlife'] as Faction[]).map(f => (
          <button
            key={f}
            className={`${styles.factionTab} ${tab === f ? styles.factionTabActive : ''}`}
            onClick={() => setTab(f)}
            style={tab === f ? { color: FACTION_COLOR[f], boxShadow: `inset 0 0 0 0.5px ${FACTION_COLOR[f]}` } : undefined}
          >
            {factionLabels[f]}
          </button>
        ))}
      </div>

      <div className={styles.heroesGrid}>
        {heroes.length === 0 && (
          <div className={styles.empty}>{tr('Героев фракции нет в данных', 'No heroes for this faction in data')}</div>
        )}
        {heroes.map(h => {
          const owned = heroesOwned.has(h.id);
          return (
            <button
              key={h.id}
              className={`${styles.heroCard} cut-m`}
              style={{
                boxShadow: `inset 0 0 0 0.5px ${owned ? FACTION_COLOR[h.faction] : 'var(--line)'}`,
                opacity: owned ? 1 : 0.6
              }}
              onClick={onHero(h)}
            >
              <HeroAvatar id={h.id} size={56} faction={h.faction} />
              <div className={styles.heroName}>{trHeroName(h.id, h.name_ru)}</div>
              <div className={styles.heroStats}>
                <span style={{ color: '#ff8e8e' }}>{h.atk}</span>{' / '}
                <span style={{ color: '#ffd56a' }}>{h.armor}</span>{' / '}
                <span style={{ color: '#74e398' }}>{h.hp}</span>
              </div>
              <div className={styles.heroStatus} style={{ color: owned ? 'var(--neon-li)' : 'var(--t-lo)' }}>
                {owned ? tr('✓ ЕСТЬ', '✓ OWNED') : tr('🔒 НЕТ', '🔒 LOCKED')}
              </div>
            </button>
          );
        })}
      </div>
    </ScreenLayout>
  );
}

// (Этап 40) Редактор колоды переехал в отдельный файл DeckEditorScreen.tsx
// с полным каталогом 278 карт и работающими лимитами.

// ============================================================
// 4. КОСМЕТИКА (магазин)
// ============================================================

interface CosmeticItem { id: string; name: string; category: string; price: number; color: string }
function buildCosmetics(t: (ru: string, en: string) => string): CosmeticItem[] {
  return [
    { id: 'c1', name: t('Аватар "Кибердева"', 'Avatar "Cyber Maiden"'),  category: 'avatar', price: 4.99, color: 'var(--neon-mg)' },
    { id: 'c2', name: t('Рамка "Неон"', 'Frame "Neon"'),                 category: 'frame',  price: 2.99, color: 'var(--neon-cy)' },
    { id: 'c3', name: t('Обложка "Корп"', 'Cover "Corp"'),               category: 'back',   price: 3.99, color: 'var(--neon-am)' },
    { id: 'c4', name: t('Скин карты "Холо"', 'Card skin "Holo"'),        category: 'skin',   price: 9.99, color: 'var(--neon-li)' },
    { id: 'c5', name: t('Эмоции "Корпорат"', 'Emotes "Corporate"'),      category: 'emote',  price: 1.99, color: 'var(--neon-rd)' }
  ];
}

export function ShopCosmeticScreen() {
  const { hapticTap, hapticSuccess, hapticError, alert } = useTelegram();
  const tr = useTr();
  const buyForUsdt = useGameStore(s => s.buyForUsdt);
  const usdt = useGameStore(s => s.currencies.usdt);
  const [owned, setOwned] = useState<Set<string>>(new Set());
  const cosmetics = buildCosmetics(tr);

  const onBuy = (c: CosmeticItem) => () => {
    hapticTap();
    if (owned.has(c.id)) {
      alert(tr(`«${c.name}» уже куплено.`, `«${c.name}» already owned.`));
      return;
    }
    if (!window.confirm(tr(
      `Купить «${c.name}» за $${c.price.toFixed(2)}?`,
      `Buy «${c.name}» for $${c.price.toFixed(2)}?`
    ))) return;
    const ok = buyForUsdt(c.price, () => setOwned(prev => new Set([...prev, c.id])));
    if (!ok) {
      hapticError();
      alert(tr(
        `Не хватает USDT. Нужно $${c.price.toFixed(2)}, есть $${usdt.toFixed(2)}.`,
        `Not enough USDT. Need $${c.price.toFixed(2)}, have $${usdt.toFixed(2)}.`
      ));
    } else {
      hapticSuccess();
      alert(tr(`Куплено: ${c.name}`, `Bought: ${c.name}`));
    }
  };

  return (
    <ScreenLayout title={tr('КОСМЕТИКА', 'COSMETICS')} backTo="/shop">
      <div className={styles.cosmeticsList}>
        {cosmetics.map(c => (
          <button
            key={c.id}
            className={`${styles.cosmRow} cut-s`}
            style={{
              boxShadow: `inset 0 0 0 0.5px ${c.color}`,
              opacity: owned.has(c.id) ? 0.6 : 1
            }}
            onClick={onBuy(c)}
          >
            <div className={styles.cosmIcon} style={{ borderColor: c.color }}>
              {c.category === 'avatar' && '👤'}
              {c.category === 'frame' && '⬜'}
              {c.category === 'back' && '🂠'}
              {c.category === 'skin' && '🎴'}
              {c.category === 'emote' && '😈'}
            </div>
            <div className={styles.cosmInfo}>
              <div className={styles.cosmName}>{c.name}</div>
              <div className={styles.cosmCat}>{c.category}</div>
            </div>
            <div className={styles.cosmPrice}>
              {owned.has(c.id) ? '✓' : `$${c.price.toFixed(2)}`}
            </div>
          </button>
        ))}
      </div>
    </ScreenLayout>
  );
}
